import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ADMIN } from "@/constants/testIds";
import { Download, Search } from "lucide-react";

const STATUSES = ["All", "New", "Contacted", "Quoted", "Booked", "Completed", "Lost"];

export default function AdminLeads() {
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("All");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 100, skip: 0 };
            if (q) params.q = q;
            if (status && status !== "All") params.status = status;
            const res = await api.get("/admin/leads", { params });
            setItems(res.data.items || []);
            setTotal(res.data.total || 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

    const exportCsv = async () => {
        const token = localStorage.getItem("ys_admin_token");
        const res = await fetch(`${API_BASE}/admin/leads/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "ys-removals-leads.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-heading font-semibold flex-1">Leads <span className="text-sm text-[#17252D]/60 font-normal">({total})</span></h1>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#17252D]/50" />
                        <Input
                            data-testid={ADMIN.leadsSearch}
                            placeholder="Search by name, email, ref, postcode…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            className="pl-8 w-72"
                        />
                    </div>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger data-testid={ADMIN.leadsFilter} className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button onClick={load} variant="outline">Refresh</Button>
                    <Button data-testid={ADMIN.leadsExport} onClick={exportCsv} className="bg-[#001B3D] hover:bg-[#001B3D]/90 text-white">
                        <Download className="h-4 w-4 mr-2" /> CSV
                    </Button>
                </div>
            </div>

            <div data-testid={ADMIN.leadsTable} className="rounded-xl bg-white border border-[#17252D]/10 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#F3F7F9] text-left text-xs uppercase tracking-wide text-[#17252D]/60">
                        <tr>
                            <th className="px-4 py-2">Ref</th>
                            <th className="px-4 py-2">Created</th>
                            <th className="px-4 py-2">Name</th>
                            <th className="px-4 py-2 hidden md:table-cell">Service</th>
                            <th className="px-4 py-2 hidden md:table-cell">From / To</th>
                            <th className="px-4 py-2">Status</th>
                            <th className="px-4 py-2 hidden lg:table-cell">Contact</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td className="p-4 text-[#17252D]/60" colSpan={7}>Loading…</td></tr>}
                        {!loading && items.length === 0 && (
                            <tr><td className="p-4 text-[#17252D]/60" colSpan={7}>No leads yet.</td></tr>
                        )}
                        {items.map((l) => (
                            <tr key={l.id} data-testid={ADMIN.leadsRow} onClick={() => window.location.assign(`/admin/leads/${l.id}`)} className="border-t border-[#17252D]/10 hover:bg-[#F3F7F9] cursor-pointer">
                                <td className="px-4 py-2 font-mono"><Link to={`/admin/leads/${l.id}`} onClick={(e) => e.stopPropagation()} className="text-[#001B3D]">{l.reference}</Link></td>
                                <td className="px-4 py-2">{(l.created_at || "").slice(0, 10)}</td>
                                <td className="px-4 py-2">{l.full_name}</td>
                                <td className="px-4 py-2 hidden md:table-cell">{l.service}</td>
                                <td className="px-4 py-2 hidden md:table-cell text-xs">{l.collection_postcode || "—"} → {l.delivery_postcode || "—"}</td>
                                <td className="px-4 py-2"><span className="text-xs px-2 py-1 rounded-full border border-[#001B3D]/25 text-[#001B3D]">{l.status}</span></td>
                                <td className="px-4 py-2 hidden lg:table-cell text-xs"><a className="text-[#001B3D]" href={`tel:${l.phone}`}>{l.phone}</a> &middot; <a className="text-[#001B3D]" href={`mailto:${l.email}`}>{l.email}</a></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
